import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { of, race } from 'rxjs';
import { catchError, concatMap, defaultIfEmpty, map, take, tap, withLatestFrom } from 'rxjs/operators';
import { ChangeLogEntry, VaccinationStatus } from '@mona/models';
import {
    applyInstancesChanges,
    ChangeLogAction,
    ChangeLogSelectors,
    getPersistedChangesData,
    getRevertedChangesForModel,
    getRevertedCreateIds,
    reconcileInstancesWithChanges,
} from '@mona/pdms/data-access-changelog';
import {
    applyLastChangedByToPrescriptions,
    currentEncounterId,
    getCurrentEncounterProp,
    isEmptyEncounterId,
} from '@mona/pdms/data-access-combined';
import { PractitionersFacade } from '@mona/pdms/data-access-practitioners';
import { isSameEncounterFilter } from '@mona/shared/utils';
import { VaccinationStatusApi } from '../../infrastructure';
import { VaccinationsStatusAction } from '../actions';
import { selectVaccinationAll } from '../selectors';

/**
 * Vaccinations effects
 */
@Injectable()
export class VaccinationsEffects {
    changeLogMap$ = this.store.select(ChangeLogSelectors.getChangesMap);
    selectVaccinationAll$ = this.store.select(selectVaccinationAll);

    /** Load Vaccinations */
    loadVaccinations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VaccinationsStatusAction.loadVaccinations),
            withLatestFrom(getCurrentEncounterProp('patientId')),
            currentEncounterId(this.store),
            concatMap(([[, patientId], encounterId]) =>
                race(
                    isEmptyEncounterId(this.store).pipe(
                        take(1),
                        map(() => VaccinationsStatusAction.loadVaccinationsCancelled({ reason: 'encounter-cleared' })),
                    ),
                    this.vaccinationStatusApi.getVaccinationStatus(patientId).pipe(
                        isSameEncounterFilter(currentEncounterId(this.store), encounterId),
                        withLatestFrom(this.changeLogMap$, this.practitionersFacade.practitionersMap$),
                        map(([data, changesMap, practitionersMap]) => {
                            const changes = changesMap['VaccinationStatus'] || [];

                            if (changes.length) {
                                data = applyInstancesChanges(data, changes) as VaccinationStatus[];
                            }

                            data = applyLastChangedByToPrescriptions(data, practitionersMap);

                            return VaccinationsStatusAction.loadVaccinationsSuccess({ data });
                        }),
                        catchError(error => of(VaccinationsStatusAction.loadVaccinationsFailure({ error }))),
                        defaultIfEmpty(
                            VaccinationsStatusAction.loadVaccinationsCancelled({
                                reason: 'encounter-changed',
                            }) as Action,
                        ),
                    ),
                ),
            ),
        ),
    );

    /** Listen change save success & upsert entity + show message */
    onChangeSaved$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    ChangeLogAction.saveChangeAction.action,
                    ChangeLogAction.saveChangeAction.succeededAction,
                    ChangeLogAction.saveChangesAction.action,
                    ChangeLogAction.saveChangesAction.succeededAction,
                    ChangeLogAction.revertOptimisticChange,
                    ChangeLogAction.revertOptimisticChanges,
                    ChangeLogAction.revertStaleSavingEntries,
                ),
                withLatestFrom(this.selectVaccinationAll$, this.changeLogMap$),
                tap(([action, data, changesMap]) => {
                    const changes: ChangeLogEntry<VaccinationStatus>[] = changesMap['VaccinationStatus'] || [];
                    const reverted = getRevertedChangesForModel(action, 'VaccinationStatus');
                    const vaccinations = reconcileInstancesWithChanges(data, changes, reverted);

                    // A reverted optimistic create is filtered out of the reconciled array, but
                    // `upsertMany` never removes entities — so the phantom row would linger with
                    // its `isSaving` flag set. Explicitly remove reverted create ids (mirrors the
                    // entity-adapter effects, e.g. AdditionalDevices).
                    const revertedCreateIds = getRevertedCreateIds(reverted);
                    if (revertedCreateIds.length) {
                        this.store.dispatch(VaccinationsStatusAction.removeVaccinations({ ids: revertedCreateIds }));
                    }

                    if (vaccinations) {
                        this.store.dispatch(VaccinationsStatusAction.upsertVaccinations({ data: vaccinations }));
                    }
                }),
            ),
        { dispatch: false },
    );

    /** Listen change persist success & add one - realistic update */
    onChangePersisted$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ChangeLogAction.persistChangesAction.succeededAction),
                withLatestFrom(
                    this.selectVaccinationAll$,
                    this.changeLogMap$,
                    this.practitionersFacade.practitionersMap$,
                ),
                tap(([, data, changesMap, practitionersMap]) => {
                    const changes = changesMap['VaccinationStatus'] || [];

                    if (changes.length) {
                        const removeUpdateData = getPersistedChangesData(data, changes);
                        removeUpdateData.toUpdate = applyLastChangedByToPrescriptions(
                            removeUpdateData.toUpdate,
                            practitionersMap,
                        );

                        this.store.dispatch(
                            VaccinationsStatusAction.addVaccinationsChanges({
                                toUpdateEntities: removeUpdateData.toUpdate || [],
                                toRemoveIds: removeUpdateData.toRemove.map(({ id }) => id) || [],
                            }),
                        );
                    }
                }),
            ),
        { dispatch: false },
    );

    /**
     * Constructor
     * @param store
     * @param actions$
     * @param vaccinationStatusApi
     * @param practitionersFacade
     */
    constructor(
        private store: Store<any>,
        private actions$: Actions,
        private vaccinationStatusApi: VaccinationStatusApi,
        private practitionersFacade: PractitionersFacade,
    ) {}
}
